// Classification chip — THE shared identity element. The same fixed palette
// slot everywhere a classification appears (leaderboard, pod cards, incident
// groups, charts, Slack drafts) so colour follows the entity, never the rank.
import { classificationColor } from '../../lib/palette';
import { titleCase } from '../../lib/format';

/**
 * `count` is optional: bare chip reads "Product", counted chip reads
 * "2 product" (the board's pod-card idiom, every count from visible data).
 */
export function ClassificationChip({ classification, mode, count = null }) {
  return (
    <span className="chip">
      <span
        className="swatch"
        style={{ background: classificationColor(classification, mode) }}
        aria-hidden="true"
      />
      {count !== null ? `${count} ${classification}` : titleCase(classification)}
    </span>
  );
}

/**
 * Observed-only chip (MCIP-33). This signal has no failing test behind it: a browser flow
 * saw the backend answer 5xx and kept passing. Says so out loud, because otherwise the
 * "0 blocked tests" on the card reads as a bug in the blast-radius math.
 */
export function ObservedOnlyChip({ observedOnly }) {
  if (!observedOnly) return null;
  return (
    <span className="tag tag--observed" title="no test failed — a passing browser flow witnessed the backend error">
      Observed only
    </span>
  );
}

/**
 * Origin chip (MCIP-33). Silent for the normal case (CI only). It appears when a local
 * suite run witnessed the same fault — evidence that reinforces the signal without ever
 * having moved a pass rate — and says so plainly when CI has NOT seen it, because that is
 * the case a reader would otherwise misjudge as fleet-wide.
 */
export function OriginChip({ origins }) {
  if (!Array.isArray(origins) || !origins.includes('local')) return null;
  const ciToo = origins.includes('ci');
  return (
    <span
      className="tag tag--origin-local"
      title={ciToo
        ? 'witnessed in CI and reproduced by a local suite run'
        : 'witnessed only by a local suite run — no CI run has seen it, so it is not raised as an incident'}
    >
      {ciToo ? '+ local' : 'local only'}
    </span>
  );
}

/** Release-at-failure chip (MCIP-24) — monospace, because a version is verbatim data. */
export function VersionChip({ version, artifact = null, title = 'release at failure', source = null, observed = [] }) {
  if (!version) return null;
  // Rolling deploys stamp several versions inside ONE run — show the span,
  // not a single number pretending otherwise. Inherited versions (no version
  // in this run; the signal's newest stamped failure supplies one) render
  // with ≈ so they can't read as this run's release.
  const span = observed.length > 1 ? `${observed[0]}→${observed[observed.length - 1]}` : version;
  const inherited = source === 'inherited';
  const label = inherited ? `≈ ${version}` : span;
  const hint = inherited
    ? 'from the signal\'s newest stamped failure — this run carries no version'
    : source === 'resolved'
      ? 'resolved from the release service\'s span versions around the run start (Datadog) — the release active in this environment, not read from the run\'s own artifacts'
      : observed.length > 1
        ? `versions observed during this run (rolling deploy): ${observed.join(', ')}`
        : title;
  return (
    <span className="tag tag--ver" title={hint}>
      {artifact ? `${artifact} ${label}` : label}
    </span>
  );
}
